import { atom, RecoilValueReadOnly, selector, selectorFamily } from 'recoil'

import { keyFactory } from 'src/utils/recoil'
import * as buildoutFrameAtoms from 'src/atoms/buildout-frame'
import * as radiusRingsAtoms from 'src/atoms/radius-rings'
import * as driveTimeAtoms from 'src/atoms/drive-time'
import * as insightsAtoms from 'src/components/insights/atoms'
import * as demoTableAtoms from 'src/atoms/demo-table'
import { MapOverlay } from 'src/components/buildout-frame/types'
import { TableFormat } from 'src/components/insights/true-trade-area/overview/constants'
import { formatChartData } from 'src/components/demo-table/utils'
import { DemographicDataPoint, DemoRadiiResponse, Position } from 'src/common-services'
import { googlePlace } from 'src/atoms/search'

export enum TradeAreaType {
  FOOT_TRAFFIC,
  DRIVE_TIME,
  RADIUS_RINGS,
  CUSTOM_POLYGON,
}

const key = keyFactory('buildout')

export const tradeAreaType = atom<TradeAreaType>({
  key: key('trade-area-type'),
  default: TradeAreaType.RADIUS_RINGS,
})

export const ttaCenterPoint = atom<Position | undefined>({
  key: key('tta-center-point'),
  default: undefined,
})

export const ttaPosition = selector<Position | undefined>({
  key: key('tta-position'),
  get: ({ get }) => {
    const centerPoint = get(ttaCenterPoint)
    if (centerPoint) {
      return centerPoint
    }
    const place = get(googlePlace)
    return place?.geometry.location
  },
})

export type ChartData = Record<
  string,
  {
    type?: string
    currentYear?: number
    fiveYear?: number
  } & {
    nationalAvg?: number
  }
>

export const buildoutChartData = selectorFamily<
  ChartData[],
  { dataPoint: string; percentUnit: number[]; format: TableFormat }
>({
  key: key('buildout-chart-data'),
  get:
    ({ dataPoint, percentUnit, format }) =>
    ({ get }) => {
      const overlay = get(buildoutFrameAtoms.mapOverlay)
      switch (overlay) {
        case MapOverlay.DriveTime:
          return get(driveTimeChartData({ dataPoint, percentUnit, format }))
        case MapOverlay.RadiusRings:
          return get(radiusRingsChartData({ dataPoint, percentUnit, format }))
        case MapOverlay.TrueTradeArea:
          return get(insightsAtoms.ttareaDemographicsChartData({ dataPoint, percentUnit, format }))
      }
    },
})

type DataPoint = Record<
  string,
  Record<
    string,
    {
      type?: string | undefined
      currentYear?: number | undefined
      fiveYear?: number | undefined
    }
  >
>

const setDataPointForGeneralOverview = (dataPoints: DataPoint) => {
  let base = {}
  for (const key in dataPoints || {}) {
    base = { ...base, ...dataPoints[key] }
  }
  return base
}

export const driveTimeChartData = selectorFamily<
  ChartData[],
  { dataPoint: string; percentUnit: number[]; format: TableFormat; areas?: number[] }
>({
  key: key('drive-time-chart-data'),
  get:
    ({ dataPoint, percentUnit, format }) =>
    ({ get }) => {
      const data = get(driveTimeAtoms.demographicsData)
      if (!data) {
        return []
      }
      const tableOptions = get(demoTableAtoms.tableOptions)
      return data.map((datum, index) => {
        const dataPoints = datum?.data
        let base = dataPoints?.[dataPoint]
        if (dataPoint === DemographicDataPoint.GeneralOverview) {
          base = setDataPointForGeneralOverview(dataPoints)
        }
        return formatChartData({
          currentDataPoint: dataPoint,
          dataPoint: base,
          tableOptions,
          percentUnit: percentUnit[index],
          format,
          radius: datum.radius,
        })
      })
    },
})

export const radiusRingsChartData = selectorFamily<
  ChartData[],
  { dataPoint: string; percentUnit: number[]; format: TableFormat }
>({
  key: key('radius-rings-chart-data'),
  get:
    ({ dataPoint, percentUnit, format }) =>
    ({ get }) => {
      const data = get(radiusRingsAtoms.demographicsData({ position: undefined, compare: false }))
      if (!data) {
        return []
      }
      const tableOptions = get(demoTableAtoms.tableOptions)
      return data.map((datum, index) => {
        const dataPoints = datum?.data
        let base = dataPoints?.[dataPoint]
        if (dataPoint === DemographicDataPoint.GeneralOverview) {
          base = setDataPointForGeneralOverview(dataPoints)
        }
        return formatChartData({
          currentDataPoint: dataPoint,
          dataPoint: base,
          tableOptions,
          percentUnit: percentUnit[index],
          format,
          radius: datum.radius,
        })
      })
    },
})

export const buildoutData = selectorFamily<
  Record<string, Record<string, number>>,
  { currentDataPoint: string; format: TableFormat }
>({
  key: key('buildout-data'),
  get:
    ({ currentDataPoint, format }) =>
    ({ get }) => {
      const overlay = get(buildoutFrameAtoms.mapOverlay)
      switch (overlay) {
        case MapOverlay.DriveTime:
          return get(buildoutTableData({ currentDataPoint, format, atom: driveTimeAtoms.demographicsData }))
        case MapOverlay.RadiusRings:
          const position = get(ttaPosition)
          return get(
            buildoutTableData({
              currentDataPoint,
              format,
              atom: radiusRingsAtoms.demographicsData({ position, compare: false }),
            })
          )
        case MapOverlay.TrueTradeArea:
          return get(insightsAtoms.overviewTableColumns({ dataPoint: currentDataPoint, format }))
      }
    },
})

export const buildoutTableData = selectorFamily<
  Record<string, Record<string, number>>,
  { currentDataPoint: string; format: TableFormat; atom: RecoilValueReadOnly<DemoRadiiResponse | undefined> }
>({
  key: key('buildout-table-data'),
  get:
    ({ currentDataPoint, format, atom }) =>
    ({ get }) => {
      const data = get(atom)
      const cols: Record<string, Record<string, number>> = {
        0: {},
        1: {},
        2: {},
      }
      for (const col in cols) {
        const colData = data?.[Number(col)]?.data || {}
        let base = colData[currentDataPoint] || {}
        if (currentDataPoint === DemographicDataPoint.GeneralOverview) {
          base = {
            'Retail Spend': {
              currentYear: data?.[Number(col)]?.consumerSpendData?.currentYear?.total_retail,
              fiveYear: data?.[Number(col)]?.consumerSpendData?.fiveYear?.total_retail,
            },
          }
          for (const dataPoint in colData || {}) {
            base = { ...base, ...colData[dataPoint] }
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { title, showFloatingTabs, dataInPercentages, ...rows } = format
        const flatRows = Object.values(rows).flat()
        for (const row of flatRows) {
          let datum = (base[row] as Record<string, number>)?.currentYear
          if (row === 'Non Hispanic') {
            datum = 100 - (base['% Hispanic'] as Record<string, number>).currentYear
          }
          cols[col][row] = datum
        }
      }
      return cols
    },
})
